import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { PrescriptionV3PrescriptionTypeEnum } from '@redteclab/api/clients/bully'
import { IconInfo } from '@redteclab/icons'

import { useActivateZeroEuroCopaymentExperiment } from '../../../../experiments/zero-euro-copayment-eligibility/useActivateZeroEuroCopaymentExperiment'
import { useZeroEuroCopaymentEligibility } from '../../../../experiments/zero-euro-copayment-eligibility/ZeroEuroCopaymentEligibilityProvider'
import { useOpenableControls } from '../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../global-config'
import { i18nGetCurrencyForLocale } from '../../../../i18n'
import { priceGetFormattedValueDiscount } from '../../../../price'
import { ProductPrescriptionZeroCopaymentDialog } from '../../../../product'
import { prescriptionAccessorGetPrescriptionLegalNote } from '../../../model/prescriptionAccessor/prescriptionAccessorGetPrescriptionLegalNote'
import { useProductPageContext } from '../../productPageContext'
import { ProductPrescriptionBonusDialog } from '../../ProductPrescriptionBonusDialog/ProductPrescriptionBonusDialog'

export const ProductBuyBoxPrescriptionBonusBadge: FC = () => {
  const modalControls = useOpenableControls()
  const { currentOffer, currentPrescriptionType } = useProductPageContext()
  const { superScripts } = useGlobalConfigContext()
  const intl = useIntl()
  const isZeroEuroCopaymentEligible = useZeroEuroCopaymentEligibility()

  const bonus =
    currentOffer?.prescriptionPriceMap?.[currentPrescriptionType]?.bonus

  const isPrivatePrescription =
    currentPrescriptionType ===
    PrescriptionV3PrescriptionTypeEnum.PrivatePrescription
  const isPublicPrescription =
    currentPrescriptionType ===
      PrescriptionV3PrescriptionTypeEnum.InsurancePrescription ||
    currentPrescriptionType ===
      PrescriptionV3PrescriptionTypeEnum.FreePrescription
  const isZeroCopaymentPrescription =
    currentPrescriptionType ===
      PrescriptionV3PrescriptionTypeEnum.InsurancePrescription &&
    isZeroEuroCopaymentEligible
  const shouldShowPublicPrescriptionBonus =
    isPublicPrescription && !isZeroCopaymentPrescription
  const isZeroCopaymentRendered =
    isZeroCopaymentPrescription && Boolean(bonus && bonus > 0)

  useActivateZeroEuroCopaymentExperiment(isZeroCopaymentRendered)

  if (!bonus || bonus <= 0) {
    return null
  }

  return (
    <>
      <button
        className="flex gap-1 self-start rounded-l bg-light-secondary-medium pl-0.5 pr-1 tablet:mb-2"
        data-qa-id="product-buy-box-prescription-bonus-badge"
        onClick={modalControls.handleOpen}
        type="button"
      >
        <span className="my-0.5 rounded-l border border-dark-primary-min bg-light-primary-low px-3 py-1 text-xs font-medium text-dark-brand">
          {isZeroCopaymentPrescription
            ? intl.formatNumber(0, {
                currency: i18nGetCurrencyForLocale(intl.locale),
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                style: 'currency',
              })
            : priceGetFormattedValueDiscount(bonus, intl)}
        </span>
        <span className="my-auto">
          {isPrivatePrescription ? (
            <FormattedMessage
              id="general.prescriptionBonus.privatePrescription"
              values={{
                sup: (
                  <sup key="privateBonusBadge">
                    {prescriptionAccessorGetPrescriptionLegalNote(
                      currentPrescriptionType,
                      superScripts,
                    )}
                  </sup>
                ),
              }}
            />
          ) : null}
          {shouldShowPublicPrescriptionBonus ? (
            <FormattedMessage
              id="general.prescriptionBonus.publicPrescription"
              values={{
                sup: (
                  <sup key="publicBonusBadge">
                    {prescriptionAccessorGetPrescriptionLegalNote(
                      currentPrescriptionType,
                      superScripts,
                    )}
                  </sup>
                ),
              }}
            />
          ) : null}
          {isZeroCopaymentPrescription ? (
            <FormattedMessage id="general.label.additionalCharge" />
          ) : null}
        </span>
        <span className="my-auto">
          <IconInfo className="size-4" />
        </span>
      </button>
      {isZeroCopaymentPrescription ? (
        <ProductPrescriptionZeroCopaymentDialog
          isOpen={modalControls.isOpen}
          onClose={modalControls.handleClose}
        />
      ) : (
        <ProductPrescriptionBonusDialog
          isOpen={modalControls.isOpen}
          onClose={modalControls.handleClose}
        />
      )}
    </>
  )
}
